// Vuelidate validators
import { required, email , numeric, integer,  alpha,  url,  minLength,  maxLength, minValue, maxValue } from 'vuelidate/lib/validators';

// Date-Fns helpers
import { formatISO, isBefore, parseISO, isDate, isAfter } from 'date-fns';

export const vuelidateUtil = {
	setFieldsRequired(fieldNames, isRequired, selectedRequiredFields) {
		let requiredFields = {};
		for (let fieldName in fieldNames) {
			if (isRequired) {
				requiredFields[fieldName] = { required };
			} else {
				if (selectedRequiredFields.length > 0 && selectedRequiredFields.includes(fieldName)) {
					requiredFields[fieldName] = { required };
				} else {
					requiredFields[fieldName] = { };
				}
			}
		}
		return requiredFields;
	},
	setRequiredValidation(object, fieldName) {
		// required
		if (object[fieldName]) {
			object[fieldName].required = required;
		}

		return object;
	},
	setIsPastDate(object, fieldName) {
		if (object[fieldName]) {
			object[fieldName].isPastDate = isPastDate;
		}

		return object;
	},
	setIsFutureDate(object, fieldName) {
		if (object[fieldName]) {
			object[fieldName].isFutureDate = isFutureDate;
		}

		return object;
	},
	setIsFutureDateAndTime(object, fieldName) {
		if (object[fieldName]) {
			object[fieldName].isFutureDateAndTime = isFutureDateAndTime;
		}

		return object;
	},
	setEmailValidation(object, fieldName) {
		// email
		if (object[fieldName]) {
			object[fieldName].email = email;
		}
		return object;
	},
	setNumericValidation(object, fieldName) {
		// Accepts only numerics.
		if (object[fieldName]) {
			object[fieldName].numeric = numeric;
		}
		return object;
	},
	setIntegerValidation(object, fieldName) {
		// Accepts positive and negative integers.
		if (object[fieldName]) {
			object[fieldName].integer = integer;
		}
		return object;
	},
	setAlphaValidation(object, fieldName) {
		// Accepts only alphabet characters.
		if (object[fieldName]) {
			object[fieldName].alpha = alpha;
		}
		return object;
	},
	setURLValidation(object, fieldName) {
		// Accepts only URLs.
		if (object[fieldName]) {
			object[fieldName].url = url;
		}
		return object;
	},
	setMinLengthValidation(object, fieldName, value) {
		// Requires the input to have a minimum specified length, inclusive. Works with arrays.
		if (object[fieldName]) {
			object[fieldName].minLength = minLength(value);
		}
		return object;
	},
	setMaxLengthValidation(object, fieldName, value) {
		// Requires the input to have a maximum specified length, inclusive. Works with arrays.
		if (object[fieldName]) {
			object[fieldName].maxLength = maxLength(value);
		}
		return object;
	},
	setHasUpperCase(object, fieldName) {
		// Requires the input to have at least 1 Capital Letter
		if (object[fieldName]) {
			object[fieldName].hasUpperCase = hasUpperCase;
		}

		return object;
	},
	setHasNumber(object, fieldName) {
		//  Requires the input to have at least 1 Number
		if (object[fieldName]) {
			object[fieldName].hasNumber = hasNumber;
		}

		return object;
	},
	setPetNameLength(object, fieldName) {
		if (object[fieldName]) {
			object[fieldName].petNameLength = petNameLength;
		}

		return object;
	},
	setChecked(object, fieldName) {
		if (object[fieldName]) {
			object[fieldName].checked = value => value === true
		}
		return object;
	},
	/*
	Comma sep emails helper methods
	*/
	setMultipleEmailValidation(object, fieldName) {
		if (object[fieldName]) {
			object[fieldName] = {
				$each: {
					required,
					email
				}
			}
		}

		return object;
	},
	splitEmailAddresses(emailAddress) {
		let list = emailAddress.split(',');
		let emailListTrimmed = [];

		// push and trims the values
		for (let i = 0; i < list.length; i++) {
			let item = list[i].trim();
			emailListTrimmed.push(item);
		}

		return emailListTrimmed;
	},
	setIsFutureNextYear(object, fieldName) {
		if (object[fieldName]) {
			object[fieldName].isFutureNextYearDate = isFutureNextYearDate;
		}

		return object;
	},
	setMinValueValidation(object, fieldName, value) {
		// Requires the input to have a minimum value
		if (object[fieldName]) {
			object[fieldName].minValue = minValue(value);
		}
		return object;
	},
	setMaxValueValidation(object, fieldName, value) {
		// Requires the input to have a maximum value
		if (object[fieldName]) {
			object[fieldName].maxValue = maxValue(value);
		}
		return object;
	}
}

export function isFutureNextYearDate(date) {
	const dateNextYear = new Date(new Date().setFullYear(new Date().getFullYear(), 12, 0));
	let isValid = true;

	if (date.trim() !== '') {
		isValid = new Date(date) < dateNextYear;
	}

	return isValid;
}

export function isPastDate(date) {
	let isValid = false;
	let dateToday = formatISO(new Date(), {representation: 'date'});

	if (!date) {
		return isValid;
	}

	if (isDate(date)) {
		isValid = !isBefore(date, parseISO(dateToday));
	} else {
		let parsedDate = formatISO(new Date(date), {representation: 'date'});
		isValid = !isBefore(parseISO(parsedDate), parseISO(dateToday));
	}

	return isValid;
}

export function isFutureDate(date) {
	let isValid = false;
	let dateToday = formatISO(new Date(), {representation: 'date'});

	if (!date) {
		return isValid;
	}

	if (isDate(date)) {
		isValid = !isAfter(date, parseISO(dateToday));
	} else {
		let parsedDate = formatISO(new Date(date), {representation: 'date'});
		isValid = !isAfter(parseISO(parsedDate), parseISO(dateToday));
	}

	return isValid;
}

export function isFutureDateAndTime(date) {
	let isValid = false;
	let dateToday = formatISO(new Date());

	if (!date) {
		return isValid;
	}

	if (isDate(date)) {
		isValid = isAfter(date, parseISO(dateToday));
	} else {
		let parsedDate = formatISO(new Date(date));
		isValid = isAfter(parseISO(parsedDate), parseISO(dateToday));
	}
	return isValid;
}

export function hasUpperCase(password) {
	let validator = password.match(/[A-Z]/);
	let isValid = false;

	// check if Validator is not Null and has Capital Letter
	if (validator && validator.length >= 1) {
		isValid = true;
	}

	return isValid;
}

export function hasNumber(password) {
	let validator = password.match(/[0-9]/);
	let isValid = false;

	// check if Validator is not Null and has Number
	if (validator && validator.length >= 1) {
		isValid = true;
	}

	return isValid;
}

export function petNameLength(petName) {
	let isValid = false;

	if (petName.length <= 12) {
		isValid = true;
	}

	return isValid;
}